import { AIRCRAFT_TYPES } from '../data/aircraft';
import { maintainLowConditionAircraft, aircraftMaintenanceCostWithCount } from './commands';
import { addAdvancedAiManager, addAiManagers, addEliteAiManager, addStrategicAiManager } from './aiManagers';
import { isAircraftDelivered } from '../simulation/leasing';
import type { AircraftType, GameState, SinglePlayerDifficulty } from './types';

const BEGINNER_HIDDEN_AIRCRAFT = new Set(['E190E2', 'A320N', 'B789', 'A35K']);

export function shouldAutoMaintain(difficulty: SinglePlayerDifficulty): boolean {
  return difficulty === 'beginner' || difficulty === 'experienced';
}

export function availableAircraftTypesForDifficulty(
  difficulty: SinglePlayerDifficulty,
  aircraftTypes: readonly AircraftType[] = AIRCRAFT_TYPES,
): AircraftType[] {
  return difficulty === 'beginner'
    ? aircraftTypes.filter((type) => !BEGINNER_HIDDEN_AIRCRAFT.has(type.id))
    : [...aircraftTypes];
}

export function autoMaintainLowConditionAircraft(state: GameState): GameState {
  if (!shouldAutoMaintain(state.singlePlayerDifficulty)) return state;
  const targets = state.aircraft.filter((aircraft) => isAircraftDelivered(aircraft, state.week) && aircraft.condition < 0.8);
  if (!targets.length) return state;
  const totalCost = targets.reduce((sum, aircraft) => sum + aircraftMaintenanceCostWithCount(
    aircraft,
    state.aircraft.filter((candidate) => candidate.typeId === aircraft.typeId).length,
  ), 0);
  if (state.cash < totalCost) return state;
  return maintainLowConditionAircraft(state);
}

export function applyDifficultyMilestone(state: GameState): GameState {
  let next = state;
  const strategies = () => next.aiManagers.map((manager) => manager.strategy);
  if (next.singlePlayerDifficulty === 'beginner' && next.week === 52 && !strategies().includes('basic')) {
    next = addAiManagers(next, 1);
  }
  if (next.singlePlayerDifficulty === 'experienced' && next.week === 27 && !strategies().includes('advanced')) {
    next = addAdvancedAiManager(next);
  }
  if (next.singlePlayerDifficulty === 'expert' && next.week === 5) {
    if (!strategies().includes('advanced')) next = addAdvancedAiManager(next);
    if (!strategies().includes('strategic')) next = addStrategicAiManager(next);
    if (!strategies().includes('elite')) next = addEliteAiManager(next);
  }
  if (next.singlePlayerDifficulty === 'challenge' && next.week === 3) {
    while (next.aiManagers.filter((manager) => manager.strategy === 'elite').length < 3) {
      next = addEliteAiManager(next);
    }
  }
  return next;
}
